import styled from '@emotion/styled'
import { withVersion } from '@lune/auth/api'
import { browserOrMobile } from '@lune/common/device'
import { logResults } from '@lune/common/logger'
import {
  type MyURL,
  type NavigateFunction,
  type RootUrl
} from '@lune/common/paths'
import { Dashboard as DashboardLayout } from '@lune/dashboard'
import { type MenuItem } from '@lune/dashboard/menu'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import logo from '../../../assets/logo.png'
import { DEFAULT_CONFIG, api as apiConfig } from '../../api/config'
import { version as apiVersion } from '../../api/version'
import {
  AddAliasIcon,
  AllGroupIcon,
  OtherGroupIcon,
  PersonGroupIcon
} from '../../icons'
import { AliasNew, LoginPage, Root } from '../../paths'

const Logo = styled.img<{ size: number }>((props) => ({
  src: logo,
  width: props.size,
  height: props.size
}))

const nameTitle: string = browserOrMobile<string>({
  browser: 'Mail Manager',
  mobile: 'MailMan'
})

interface DashboardProps {
  checkUpdatesEveryMs?: number
  logoutOnIdleAfterMs?: number
  onNavigate?: NavigateFunction
  thisPage?: Location // TODO: Consolidate thisPage and location
}

export const hashFromRoot = (r: RootUrl, target: string): string =>
  `${r.root.url.toString()}${target}`

export const Dashboard: React.FC<DashboardProps> = ({
  checkUpdatesEveryMs,
  logoutOnIdleAfterMs,
  onNavigate,
  thisPage = document.location
}) => {
  const routerNavigate = useNavigate()
  const [menuItems, setMenuItems] = useState<
    Array<MenuItem & { emails: string[] }>
  >([])

  const navigate = useCallback(
    async (target: MyURL): Promise<void> => {
      if (onNavigate !== undefined) {
        await onNavigate(target)
      } else {
        routerNavigate(target)
        await Promise.resolve()
      }
    },
    [onNavigate, routerNavigate]
  )

  const buttons = useMemo(
    () => [
      {
        id: 0,
        icon: <AddAliasIcon />,
        description: 'add',
        onClick: () => {
          void navigate(`${AliasNew}${window.location.hash}`)
        }
      }
    ],
    [navigate]
  )

  useEffect(() => {
    void apiConfig.fetch
      .single(DEFAULT_CONFIG)
      .then((result) => result.realms)
      .then((result) =>
        result.map((item, i) => ({
          id: i,
          target: hashFromRoot(Root, `#${item.id}`),
          text: item.name,
          icon: <PersonGroupIcon />,
          emails: item.targets
        }))
      )
      .then((result) => [
        ...result,
        {
          id: result.length - 1 + 1,
          target: hashFromRoot(Root, '#nobody'),
          text: 'Nobody',
          icon: <OtherGroupIcon />,
          emails: []
        },
        {
          id: result.length - 1 + 2,
          target: hashFromRoot(Root, '#others'),
          text: 'Others',
          icon: <OtherGroupIcon />,
          emails: []
        },
        {
          id: result.length - 1 + 3,
          target: hashFromRoot(Root, '#all'),
          text: 'All',
          icon: <AllGroupIcon />,
          emails: []
        }
      ])
      .then(logResults('menuItems'))
      .then((result) => {
        setMenuItems(result)
      })
  }, [])

  useEffect(() => {
    if (menuItems.length === 0) {
      return
    }

    void withVersion(apiVersion, LoginPage, Root).then(async (auth) => {
      await auth.whoami().then((result) => {
        if (thisPage.hash === '' && thisPage.pathname === Root.root.url) {
          // Find the menu item that has our email exclusively
          const myMenuItem = menuItems.find((mi) =>
            mi.emails.includes(result.email)
          )
          // If available, navigate to it
          if (myMenuItem !== undefined) {
            void navigate(myMenuItem.target)
          }
        }
      })
    })
  }, [menuItems, navigate, thisPage.hash, thisPage.pathname])

  return (
    <DashboardLayout
      version={apiVersion}
      name={nameTitle}
      logo={<Logo size={40} src={logo} />}
      navigation={menuItems}
      buttons={buttons}
      loginUrl={LoginPage}
      rootUrl={Root}
      decorator={(content) => (
        <div id="content">
          <div className="margin">{content}</div>
        </div>
      )}
      onNavigate={navigate}
      location={thisPage}
      checkUpdates={{
        everyMs: checkUpdatesEveryMs
      }}
      logoffOnIdle={{
        afterMs: logoutOnIdleAfterMs
      }}
    />
  )
}
